import { useQuery } from "@tanstack/react-query";
import { Bot, ChevronRight, RefreshCw, ScrollText, Search, Tv, UserRound } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { adminApi } from "../../api/admin";
import { datasetApi } from "../../api/dataSources";
import { useAuth } from "../../auth/AuthContext";
import {
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  Drawer,
  EmptyState,
  PageHeader,
  SearchInput,
  Select,
  Switch,
  formatDateTime,
  formatRelative,
} from "../../ds";
import type { AuditLogEntry } from "../../types";
import { deviceLabel, errorText, roleLabel } from "./labels";

/** Event code -> what happened, phrased so it reads after the actor's name. */
const EVENT_LABELS: Record<string, string> = {
  login_success: "Signed in",
  bootstrap_admin_granted: "Confirmed platform admin rights",
  user_disabled: "Disabled a user",
  user_enabled: "Turned a user back on",
  role_assigned: "Gave someone a role",
  role_removed: "Removed someone's role",
  tenant_created: "Allowed a new organization",
  tenant_enabled: "Allowed an organization again",
  tenant_disabled: "Blocked an organization",

  tv_activation_requested: "A TV requested activation",
  device_activated: "Approved a TV",
  device_updated: "Changed a TV's details",
  device_disabled: "Disabled a TV",
  device_revoked: "Revoked a TV's access",
  device_token_rotated: "Rotated a TV's sign-in token",
  tv_device_self_revoked: "A TV signed itself out",
  device_group_created: "Created a screen group",
  device_group_updated: "Changed a screen group",

  report_created: "Created a report",
  report_updated: "Edited a report's details",
  report_duplicated: "Duplicated a report",
  report_archived: "Archived a report",
  report_restored: "Restored a report",
  report_deleted: "Deleted a report",
  report_rollback: "Rolled a report back to an earlier version",
  report_permissions_updated: "Changed who can see a report",
  report_applications_updated: "Changed which apps show a report",
  publication_succeeded: "Published a report",
  publication_failure: "Tried to publish a report (it failed checks)",
  powerbi_import: "Imported a Power BI report",

  data_source_created: "Connected a data source",
  data_source_updated: "Changed a data source",
  data_source_enabled: "Turned on a data source",
  data_source_disabled: "Turned off a data source",
  data_source_credential_updated: "Updated a data source's password or key",
  dataset_created: "Created a dataset",
  dataset_updated: "Changed a dataset",
  dataset_refresh: "Refreshed a dataset",
  dataset_fields_updated: "Changed a dataset's fields",
  dataset_fields_reconciled: "Synced a dataset's fields with its source",
  dataset_measure_created: "Added a calculation to a dataset",
  dataset_relationship_created: "Linked two datasets",
  dataset_relationship_deleted: "Removed a link between datasets",
  dataset_transform_steps_updated: "Changed a dataset's clean-up steps",

  application_created: "Created an app",
  application_updated: "Changed an app",
  application_members_updated: "Changed an app's members",
  application_client_key_regenerated: "Replaced an app's key",
};

const TARGET_TYPES: Record<string, string> = {
  user: "Person",
  device: "TV",
  device_group: "Screen group",
  report: "Report",
  dataset: "Dataset",
  data_source: "Data source",
  tenant: "Organization",
  application: "App",
};

function eventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

/** A short extra fact pulled from the event's context, if there's one worth showing. */
function eventDetail(e: AuditLogEntry): { text: string; tone?: "danger" | "success" } | null {
  const c = e.context ?? {};
  if (typeof c.role === "string") return { text: roleLabel(c.role) };
  if (e.event === "dataset_refresh" && typeof c.status === "string") return c.status === "success" ? null : { text: c.status === "failed" ? "Failed" : String(c.status), tone: "danger" };
  if (typeof c.version === "number") return { text: `Version ${c.version}` };
  return null;
}

function shortId(id: string): string {
  return id.replace(/-/g, "").slice(0, 8).toUpperCase();
}

type Actor = { name: string; kind: "user" | "device" | "system" | "unknown" };

export function AuditPage() {
  const { hasRole } = useAuth();
  const isPlatformAdmin = hasRole("platform_admin");
  const audit = useQuery({ queryKey: ["admin", "audit"], queryFn: adminApi.listAuditLogs });
  const users = useQuery({ queryKey: ["admin", "users"], queryFn: adminApi.listUsers });
  const devices = useQuery({ queryKey: ["admin", "devices"], queryFn: adminApi.listDevices });
  const reports = useQuery({ queryKey: ["admin", "reports"], queryFn: adminApi.listReports });
  const groups = useQuery({ queryKey: ["admin", "device-groups"], queryFn: adminApi.listDeviceGroups });
  const datasets = useQuery({ queryKey: ["admin", "datasets"], queryFn: datasetApi.list });
  const tenants = useQuery({ queryKey: ["admin", "tenants"], queryFn: adminApi.listTenants, enabled: isPlatformAdmin });

  const [search, setSearch] = useState("");
  const [eventType, setEventType] = useState("");
  const [hideTvRequests, setHideTvRequests] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  const names = useMemo(() => {
    const byType: Record<string, Map<string, string>> = {
      user: new Map((users.data ?? []).map((u) => [u.id, u.display_name || u.email])),
      device: new Map((devices.data ?? []).map((d) => [d.id, deviceLabel(d)])),
      report: new Map((reports.data ?? []).map((r) => [r.id, r.name])),
      device_group: new Map((groups.data ?? []).map((g) => [g.id, g.name])),
      dataset: new Map((datasets.data ?? []).map((d) => [d.id, d.name])),
      tenant: new Map((tenants.data ?? []).flatMap((t) => [[t.id, t.display_name] as [string, string], [t.tenant_id, t.display_name] as [string, string]])),
    };
    const deviceByUuid = new Map((devices.data ?? []).map((d) => [d.device_uuid, deviceLabel(d)]));
    return { byType, deviceByUuid };
  }, [users.data, devices.data, reports.data, groups.data, datasets.data, tenants.data]);

  const actorOf = (e: AuditLogEntry): Actor => {
    if (e.actor_type === "system") return { name: "The platform", kind: "system" };
    if (e.actor_type === "device") return { name: (e.actor_id && names.deviceByUuid.get(e.actor_id)) || (e.actor_id ? `New TV ${shortId(e.actor_id)}` : "A TV"), kind: "device" };
    if (e.actor_type === "user") {
      const n = (e.actor_id && names.byType.user.get(e.actor_id)) || (typeof e.context?.display_name === "string" ? e.context.display_name : null);
      return n ? { name: n, kind: "user" } : { name: "An admin", kind: "unknown" };
    }
    return { name: e.actor_type.replace(/_/g, " "), kind: "unknown" };
  };

  const targetOf = (e: AuditLogEntry): { type: string; name: string | null; id: string | null } | null => {
    if (!e.target_type) return null;
    const type = TARGET_TYPES[e.target_type] ?? e.target_type.replace(/_/g, " ");
    const name = (e.target_id && names.byType[e.target_type]?.get(e.target_id)) || null;
    return { type, name, id: e.target_id };
  };

  const all = audit.data ?? [];
  const tvRequestCount = all.filter((e) => e.event === "tv_activation_requested").length;

  const eventOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of all) counts.set(e.event, (counts.get(e.event) ?? 0) + 1);
    return [...counts.entries()].map(([event, n]) => ({ event, n, label: eventLabel(event) })).sort((a, b) => a.label.localeCompare(b.label));
  }, [all]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((e) => (eventType ? e.event === eventType : !(hideTvRequests && e.event === "tv_activation_requested")))
      .filter((e) => {
        if (!q) return true;
        const t = targetOf(e);
        const hay = [eventLabel(e.event), e.event, actorOf(e).name, t?.type ?? "", t?.name ?? "", e.target_id ?? "", e.actor_id ?? "", e.correlation_id, e.ip_address ?? ""].join(" ").toLowerCase();
        return hay.includes(q);
      });
    // actorOf/targetOf only depend on `names`
  }, [all, search, eventType, hideTvRequests, names]);

  const open = all.find((e) => e.id === openId) ?? null;
  const related = open ? all.filter((e) => e.correlation_id === open.correlation_id && e.id !== open.id) : [];
  const filtersOn = !!search || !!eventType || hideTvRequests;

  const actorCell = (e: AuditLogEntry) => {
    const a = actorOf(e);
    const icon =
      a.kind === "user" ? <Avatar name={a.name} /> : (
        <span className="ds-avatar" style={{ background: "var(--surface-2)", color: "var(--text-muted)" }}>
          {a.kind === "device" ? <Tv size={15} /> : a.kind === "system" ? <Bot size={15} /> : <UserRound size={15} />}
        </span>
      );
    return (
      <div className="ds-row" style={{ gap: 10, minWidth: 0 }}>
        {icon}
        <span className={a.kind === "unknown" ? "ds-muted ds-truncate" : "ds-truncate"} title={a.kind === "unknown" ? "Done from an admin page; the admin's name wasn't recorded for this event" : undefined}>
          {a.name}
        </span>
      </div>
    );
  };

  const targetCell = (e: AuditLogEntry) => {
    const t = targetOf(e);
    if (!t) return <span className="ds-muted">—</span>;
    return (
      <div style={{ minWidth: 0 }}>
        <div className="ds-truncate" title={t.id ?? undefined}>
          {t.name ?? (t.id ? <span className="ds-muted">{t.type} {shortId(t.id)}</span> : t.type)}
        </div>
        {t.name ? <div className="ds-small ds-muted">{t.type}</div> : null}
      </div>
    );
  };

  return (
    <div className="ds-page">
      <PageHeader
        title="Audit log"
        subtitle="A record of who did what on the platform — sign-ins, published reports, approved TVs and changes to people and data."
        actions={
          <Button icon={RefreshCw} loading={audit.isFetching && !audit.isLoading} onClick={() => void audit.refetch()}>
            Refresh
          </Button>
        }
      />

      {audit.isError ? (
        <Banner tone="danger" title="The audit log couldn't be loaded" action={<Button size="sm" icon={RefreshCw} onClick={() => void audit.refetch()}>Try again</Button>}>
          {errorText(audit.error)}
        </Banner>
      ) : (
        <>
          <div className="ds-toolbar">
            <SearchInput value={search} onChange={setSearch} placeholder="Search people, reports, TVs…" width={300} />
            <Select value={eventType} onChange={(e) => setEventType(e.target.value)} style={{ width: 280 }} aria-label="Type of activity">
              <option value="">All activity</option>
              {eventOptions.map((o) => (
                <option key={o.event} value={o.event}>
                  {o.label} ({o.n})
                </option>
              ))}
            </Select>
            <Switch
              checked={hideTvRequests}
              disabled={!!eventType}
              onChange={setHideTvRequests}
              label={<span className="ds-small">Hide TV activation requests{tvRequestCount ? ` (${tvRequestCount})` : ""}</span>}
            />
            <div className="ds-grow" />
            {audit.data && all.length ? (
              <span className="ds-small ds-muted">
                Showing {rows.length} of {all.length} latest events
              </span>
            ) : null}
          </div>
          <Card flush>
            <DataTable<AuditLogEntry>
              rows={audit.data ? rows : undefined}
              loading={audit.isLoading}
              rowKey={(e) => e.id}
              onRowClick={(e) => setOpenId(e.id)}
              initialSort={{ key: "when", dir: "desc" }}
              empty={
                all.length === 0 ? (
                  <EmptyState icon={ScrollText} title="Nothing recorded yet" description="Sign-ins, publishing, TV approvals and changes to people and data will show up here as they happen." />
                ) : (
                  <EmptyState
                    icon={Search}
                    title="No events match"
                    description={hideTvRequests && !eventType && tvRequestCount ? "TV activation requests are hidden. Show them, or try another search." : "Try another search or activity type."}
                    action={
                      filtersOn ? (
                        <Button
                          onClick={() => {
                            setSearch("");
                            setEventType("");
                            setHideTvRequests(false);
                          }}
                        >
                          Show everything
                        </Button>
                      ) : undefined
                    }
                  />
                )
              }
              columns={[
                {
                  key: "when",
                  header: "When",
                  width: 130,
                  sortValue: (e) => e.created_at,
                  cell: (e) => (
                    <span className="ds-muted" title={formatDateTime(e.created_at)} style={{ whiteSpace: "nowrap" }}>
                      {formatRelative(e.created_at)}
                    </span>
                  ),
                },
                { key: "who", header: "Who", width: "20%", sortValue: (e) => actorOf(e).name, cell: actorCell },
                {
                  key: "what",
                  header: "What happened",
                  sortValue: (e) => eventLabel(e.event),
                  cell: (e) => {
                    const d = eventDetail(e);
                    return (
                      <div className="ds-row" style={{ gap: 8, flexWrap: "wrap" }}>
                        <span>{eventLabel(e.event)}</span>
                        {d ? <Badge tone={d.tone ?? "neutral"}>{d.text}</Badge> : null}
                      </div>
                    );
                  },
                },
                { key: "target", header: "About", width: "22%", sortValue: (e) => targetOf(e)?.name ?? targetOf(e)?.type ?? "", cell: targetCell },
                { key: "open", header: "", width: 40, align: "right", cell: () => <ChevronRight size={16} className="ds-faint" /> },
              ]}
            />
          </Card>
          {audit.data && all.length ? <p className="ds-small ds-faint" style={{ marginTop: 12 }}>The log shows the latest {all.length} events. Click a row to see its full details.</p> : null}
        </>
      )}

      <Drawer open={!!open} onClose={() => setOpenId(null)} title={open ? eventLabel(open.event) : ""} width={560}>
        {open ? (
          <div className="ds-stack" style={{ gap: 20 }}>
            <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "130px minmax(0, 1fr)", rowGap: 12, columnGap: 16 }}>
              <Detail label="When">
                {formatDateTime(open.created_at)} <span className="ds-muted">({formatRelative(open.created_at)})</span>
              </Detail>
              <Detail label="Who">
                {actorCell(open)}
                {open.actor_id ? <div className="ds-mono ds-faint" style={{ marginTop: 4 }}>{open.actor_id}</div> : null}
              </Detail>
              <Detail label="What happened">
                {eventLabel(open.event)}
                {eventDetail(open) ? <> · {eventDetail(open)?.text}</> : null}
                <div className="ds-mono ds-faint" style={{ marginTop: 4 }}>{open.event}</div>
              </Detail>
              <Detail label="About">
                {targetCell(open)}
                {open.target_id ? <div className="ds-mono ds-faint" style={{ marginTop: 4 }}>{open.target_id}</div> : null}
              </Detail>
              <Detail label="IP address">{open.ip_address ? <span className="ds-mono">{open.ip_address}</span> : <span className="ds-muted">Not recorded</span>}</Detail>
              <Detail label="Request ID">
                <span className="ds-mono">{open.correlation_id}</span>
              </Detail>
            </dl>

            <div>
              <div className="ds-label" style={{ marginBottom: 8 }}>Extra details</div>
              {open.context && Object.keys(open.context).length ? (
                <pre className="ds-mono" style={{ margin: 0, padding: 12, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 320, overflow: "auto" }}>
                  {JSON.stringify(open.context, null, 2)}
                </pre>
              ) : (
                <p className="ds-muted" style={{ margin: 0 }}>No extra details were recorded for this event.</p>
              )}
            </div>

            {related.length ? (
              <div>
                <div className="ds-label" style={{ marginBottom: 8 }}>Part of the same action</div>
                <div className="ds-card">
                  <div className="ds-list">
                    {related.map((r) => (
                      <div key={r.id} className="ds-list__row is-clickable" style={{ padding: "10px 14px" }} onClick={() => setOpenId(r.id)}>
                        <div className="ds-list__main">
                          <div className="ds-list__title ds-small">{eventLabel(r.event)}</div>
                          <div className="ds-list__meta">
                            {actorOf(r).name} · {formatRelative(r.created_at)}
                          </div>
                        </div>
                        <ChevronRight size={15} className="ds-faint" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="ds-small ds-muted" style={{ fontWeight: 600 }}>{label}</dt>
      <dd style={{ margin: 0, minWidth: 0 }}>{children}</dd>
    </>
  );
}
